import React, { lazy } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import NeedAuthDialog from 'components/NeedAuthDialog';
import LinearProgress from 'components/LinearProgress';
import { isAuthenticated } from 'helpers/authToken';
import useDataListFactory from 'services/dataList';
import questionAdvices from 'endPoints/questions_advices';
import AskIcon from 'assets/icons/gg_add.svg?react';
import { PER_PAGE_MAX_LIMIT } from 'variables/main';
const AscQuestionDialog = lazy(() => import('./AscQuestionDialog'));
const QuestionItemWrapper = lazy(() => import('./QuestionItemWrapper'));

const useDataList = (props, options) => useDataListFactory(props, options);

const LoadMoreButton = styled(Button)(({ theme }) => ({
  color: theme.palette.tertiary.main,
  padding: 0,
  minWidth: 0,
  borderRadius: 0,
  textTransform: 'none',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 400,
  margin: '0 auto 10px',
  display: 'flex',
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline'
  }
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  // Inline section action ("Додати питання", "GiveAdvice"). Sized to
  // match CopyUrlButton ("Надіслати другу") so the page reads with a
  // single inline-CTA scale: 16 px text, ~34 px tall, salad-lime fill,
  // grey-400 label, radius 8 (theme default).
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500,
  padding: '6px 24px 6px 25px',
  color: theme.palette.grey[400],
  // AskIcon ships at 24×24; clamp to 20 so the button matches the 34 px
  // height of CopyUrlButton (whose CopyIcon is 18×20).
  '& svg': {
    width: 20,
    height: 20
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: 15
  }
}));

const Title = styled(Typography)(({ type, theme }) => ({
  fontSize: 24,
  display: 'flex',
  padding: 16,
  paddingLeft: 25,
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.textDisabled.main}`,
  ...(type === 'advices'
    ? {
        borderBottom: 'none',
        marginBottom: 15
      }
    : {}),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    lineHeight: '24px',
    ...(type === 'advices'
      ? {
          [theme.breakpoints.down('sm')]: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }
      : {})
  }
}));

const Wrapper = styled('section')(({ type, slideMode, theme }) => ({
  borderRadius: 15,
  // Neutral 1px border instead of the 2px primary-green frame — the green
  // frame conflicted with the slide-mode colour system (green = positive
  // CTA). The "Ask a question" button still carries the brand colour.
  border: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: theme.palette.white.main,
  marginBottom: slideMode === 'true' ? 16 : 25,
  // In slide mode the slide content area has minimal top padding, so the
  // bordered card would sit flush against the slide top. Add breathing room
  // matching the Title-led slides.
  marginTop: slideMode === 'true' ? 16 : 0,
  // Slide-mode cards stretch to the full available slide height on desktop
  // so the bordered surface anchors the slide rather than floating at the
  // top of an otherwise empty area.
  ...(slideMode === 'true'
    ? {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('sm')]: {
          flex: 1
        }
      }
    : {}),
  // Page-mode advice list stays transparent; in slide mode the advice
  // surface adopts the same bordered card as Q&A for visual consistency.
  ...(type === 'advices' && slideMode !== 'true'
    ? {
        border: 'none',
        backgroundColor: 'transparent',
        marginBottom: 40
      }
    : {})
}));

const Content = styled('div')(({ type, theme }) => ({
  padding: 16,
  maxWidth: 1040,
  margin: '0 auto',
  ...(type === 'advices'
    ? {
        maxWidth: '100%',
        padding: 0,
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 20,
        marginBottom: 20,
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr'
        }
      }
    : {})
}));

const NotFoundMessage = styled(Typography)(({ type, theme }) => ({
  padding: 16,
  ...(type === 'advices'
    ? {
        paddingLeft: 25,
        [theme.breakpoints.down('md')]: {
          padding: 0
        }
      }
    : {})
}));

const Questions = ({ entity, type: typeProp, emptyState, hideHeader }) => {
  const location = useLocation();
  const { id: routeId } = useParams();
  const t = useTranslate('Questions');

  const [type, setType] = React.useState(typeProp || 'question');
  const [openQuestionForm, setOpenQuestionForm] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);
  const [activeQuestion, setActiveQuestion] = React.useState(null);

  // Route is `professions/:slug/:id` (optionally under `/:lc/*` on jobalto.com),
  // so `:id` is the profession ObjectId. Validate to avoid firing requests
  // on edge cases.
  const professionId = React.useMemo(
    () => (routeId && /^[a-fA-F0-9]{24}$/.test(routeId) ? routeId : null),
    [routeId]
  );
  
  const [anchor] = React.useState(location.hash);

  // Create endpoint config with conditional autoLoad
  const endpointConfig = React.useMemo(() => {
    const baseConfig = questionAdvices(entity, professionId);
    return {
      ...baseConfig,
      autoLoad: !!professionId // Only auto-load if we have a valid professionId
    };
  }, [entity, professionId]);

  const { actions, data, loading, page, perPage, count } = useDataList(
    endpointConfig,
    {
      perPage: anchor ? PER_PAGE_MAX_LIMIT : 2
    }
  );

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleCheckAuth = React.useCallback(() => {
    if (isAuthenticated()) return true;
    setNeedAuth(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setNeedAuth(false);
  }, []);

  const handleOpenForm = React.useCallback(async () => {
    if (handleCheckAuth()) {
      setType(typeProp || 'question');
      setOpenQuestionForm(true);
    }
  }, [handleCheckAuth, typeProp]);

  const handleCloseForm = React.useCallback(() => {
    setOpenQuestionForm(false);
    setActiveQuestion(null);
  }, []);

  const handleGiveAnswer = React.useCallback(
    (questionId) => {
      if (handleCheckAuth()) {
        setType('answer');
        setActiveQuestion(questionId);
        setOpenQuestionForm(true);
      }
    },
    [handleCheckAuth]
  );

  const texts = React.useMemo(
    () => ({
      question: {
        headline: t('QuestionHeadline'),
        title: t('QuestionTitle'),
        placeholder: t('MessagePlaceholder'),
        loadMore: t('MoreQuestions'),
        actionText: t('AskQuestion'),
        successTitle: t('QuestionSuccessTitle'),
        empty: t('NoQuestions'),
        send: t('SendQuestion')
      },
      answer: {
        headline: t(typeProp ? 'AdviceHeadline' : 'QuestionHeadline'),
        title: t('AnswerTitle'),
        placeholder: t('AnswerPlaceholder'),
        loadMore: t(typeProp ? 'MoreAdvices' : 'MoreQuestions'),
        actionText: t(typeProp ? 'GiveAdvice' : 'AskQuestion'),
        successTitle: t('AnswerSuccessTitle'),
        empty: t('NoQuestions'),
        send: t('SendAnswer')
      },
      advices: {
        headline: t('AdviceHeadline'),
        title: t('AdviceTitle'),
        subtitle: t('AdviceMessage'),
        placeholder: t('AdviceMessagePlaceholder'),
        loadMore: t('MoreAdvices'),
        actionText: t('GiveAdvice'),
        successTitle: t('AdviseSuccessTitle'),
        empty: t('NoAdvices'),
        titlePlaceholder: t('AdviceTitlePlaceholder'),
        send: t('SendAdvice')
      }
    }),
    [t, typeProp]
  );

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const isEmpty = !(data || []).length && !loading;

  // Slide-mode callers pass `emptyState` (a beautified SlideEmptyState
  // element) and `hideHeader` (so the empty illustration carries the title
  // instead of competing with a bare "Personal advice" + "Add advice" row
  // floating at the top of the slide).
  const showRichEmpty = isEmpty && emptyState;

  return (
    <Wrapper type={type} slideMode={hideHeader ? 'true' : 'false'}>
      {hideHeader && showRichEmpty ? null : (
        <Title variant={'h2'} type={type}>
          {texts[type].headline}
          <ButtonStyled
            variant="contained"
            color="primary"
            onClick={handleOpenForm}
            endIcon={<AskIcon />}>
            {texts[type].actionText}
          </ButtonStyled>
        </Title>
      )}

      {isEmpty ? (
        showRichEmpty ? (
          React.cloneElement(emptyState, { onAction: emptyState.props.onAction || handleOpenForm })
        ) : (
          <NotFoundMessage type={type}>{texts[type].empty}</NotFoundMessage>
        )
      ) : (
        <Content type={type}>
          {(data || []).map((item, index) => (
            <QuestionItemWrapper
              key={item._id}
              index={index}
              type={type}
              entity={entity}
              handleCheckAuth={handleCheckAuth}
              handleGiveAnswer={handleGiveAnswer}
              item={item}
              actions={actions}
            />
          ))}
        </Content>
      )}

      {loading || !data ? <LinearProgress /> : null}

      {limitReached ? null : (
        <LoadMoreButton type={type} onClick={handleChangePage}>
          {texts[type].loadMore}
        </LoadMoreButton>
      )}

      <AscQuestionDialog
        open={openQuestionForm}
        activeQuestion={activeQuestion}
        handleClose={handleCloseForm}
        texts={texts[type]}
        type={type}
        entity={entity}
        actions={actions}
      />

      <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />
    </Wrapper>
  );
};

export default Questions;
